import React, { useMemo } from 'react';
import { Box, ChakraProvider, Heading, Text, extendTheme } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { withI18NextTranslate } from 'sp-ui';
import { LoadingIndicator } from 'ts/common/components';
import ClientPageBanner from 'ts/common/components/ClientPageBanner';
import ClientPageLayout from 'ts/common/components/ClientPageLayout';
import { getTheme } from 'ts/common/components/gallery';
import Questionnaire from 'ts/common/components/questionnaire/Questionnaire';
import { useTranslate } from 'ts/common/hooks';
import { useBootstrapData, withBootstrapData } from 'client_react/bootstrap';
import QuestionnaireEmailForm from 'client_react/questionnaire/components/QuestionnaireEmailForm';
import useQuestionnaire from 'client_react/questionnaire/hooks/useQuestionnaire';

const QuestionnaireAppContent: React.FC = () => {
    const t = useTranslate('clientQuestionnaire');

    const { questionnairePublicId } = useParams<{ questionnairePublicId: string }>();

    const {
        questionnaire,
        fetchQuestionnaire,
        loading,
        saveQuestionResponse,
        saving,
        submitQuestionnaire,
        submitting,
        requestFailed,
        error
    } = useQuestionnaire({ questionnairePublicId });

    const theme = useMemo(
        () => (questionnaire ? extendTheme(getTheme(questionnaire.brandTheme)) : extendTheme()),
        [questionnaire]
    );

    return (
        <ChakraProvider resetCSS={false} theme={theme}>
            {questionnaire ? (
                <ClientPageLayout
                    banner={<ClientPageBanner>{questionnaire.name}</ClientPageBanner>}
                    headingText={questionnaire.brandName}
                    showLegalLinks
                >
                    {'pages' in questionnaire ? (
                        <Questionnaire
                            error={error}
                            questionnaire={questionnaire}
                            onSave={saveQuestionResponse}
                            onSubmit={submitQuestionnaire}
                            requestFailed={requestFailed}
                            saving={saving}
                            submitting={submitting}
                        />
                    ) : (
                        <QuestionnaireEmailForm
                            error={error}
                            fetchQuestionnaire={fetchQuestionnaire}
                            loading={loading}
                        />
                    )}
                </ClientPageLayout>
            ) : loading ? (
                <LoadingIndicator />
            ) : error?.status === 404 ? (
                <Box>
                    <Heading as="h1">{t('questionnaireNotFound')}</Heading>
                    <Text>{t('questionnaireNotFoundMessage')}</Text>
                </Box>
            ) : error ? (
                <Box>
                    <Heading as="h1">{t('errorLoadingQuestionnaireTitle')}</Heading>
                    <Text>{t('errorLoadingQuestionnaire')}</Text>
                </Box>
            ) : (
                // useFetch with defer:true starts with loading false until performFetch runs;
                // avoid flashing the error UI before the first request has been attempted.
                <LoadingIndicator />
            )}
        </ChakraProvider>
    );
};

const QuestionnaireApp: React.FC = withBootstrapData(() => {
    const { translations } = useBootstrapData();

    const PageWithTranslations = useMemo(
        () => withI18NextTranslate(QuestionnaireAppContent, translations),
        [translations]
    );

    return <PageWithTranslations />;
});

export default QuestionnaireApp;
